<template>
    <div>
        <h5>Aktiva fält <i class="fal fa-square-check active-color"></i></h5>
        <sb-table-lite
            :fields="fields"
            :items="selectedFieldValues"
            :busy="loadingTables"
            striped
            hover
            small
            responsive
        >
            <template #table-busy>
                <div class="text-center text-danger my-2">
                    <SbSpinner class="align-middle"></SbSpinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(links)="row">
                <div class="d-flex" style="gap: 10px">
                    <a
                        v-if="row.index < values.length - 1"
                        @click="handleMoveDown(row.index)"
                        type="button"
                        ><i class="fal fa-arrow-down"></i
                    ></a>
                    <a
                        v-if="row.index > 0"
                        @click="handleMoveUp(row.index)"
                        type="button"
                        ><i class="fal fa-arrow-up"></i
                    ></a>
                    <a size="sm" @click="row.toggleDetails" type="button"
                        ><i class="fal fa-edit"></i
                    ></a>
                    <a
                        size="sm"
                        @click="moveToAvailable(row.index)"
                        type="button"
                        ><i class="fal fa-minus"></i
                    ></a>
                </div>
            </template>
            <template #cell(settings)="row">
                <div class="d-flex" style="gap: 10px">
                    <i
                        v-bind:class="{ 'active-color': row.item.required }"
                        class="fal fa-octagon-exclamation"
                    ></i>
                </div>
            </template>
            <template #row-details="row">
                <form-field
                    :key="row.id"
                    :inputTypes="inputTypes"
                    :attribute="row.item"
                    :quoteFields="availableQuoteFields"
                    @submitted="(payload) => handleFieldEdit(row, payload)"
                    @cancelled="row.toggleDetails"
                />
            </template>
        </sb-table-lite>

        <hr />

        <h5>Inaktiva fält <i class="fal fa-square-check"></i></h5>
        <sb-table-lite
            :fields="fields"
            :items="availableFieldValues"
            :busy="loadingTables"
            striped
            hover
            small
            responsive
        >
            <template #table-busy>
                <div class="text-center text-danger my-2">
                    <SbSpinner class="align-middle"></SbSpinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(links)="row">
                <div class="d-flex" style="gap: 10px">
                    <a
                        size="sm"
                        @click="moveToSelected(row.index)"
                        type="button"
                        ><i class="fal fa-plus"></i
                    ></a>
                    <a
                        size="sm"
                        @click="removeFromAvailable(row.index)"
                        type="button"
                        ><i class="fal fa-trash"></i
                    ></a>
                </div>
            </template>
            <template #cell(settings)="row">
                <div class="d-flex" style="gap: 10px">
                    <i
                        v-bind:class="{ 'active-color': row.item.required }"
                        class="fal fa-octagon-exclamation"
                    ></i>
                </div>
            </template>
            <template #row-details="row">
                <form-field
                    :key="row.id"
                    :inputTypes="inputTypes"
                    :attribute="row.item"
                    @submitted="(payload) => handleFieldEdit(row, payload)"
                    @cancelled="row.toggleDetails"
                />
            </template>
        </sb-table-lite>

        <div>
            <form-field
                :key="-1"
                v-if="creating"
                :inputTypes="inputTypes"
                :attribute="customFieldTemplate()"
                :quoteFields="availableQuoteFields"
                @submitted="(payload) => handleFieldCreated(payload)"
                @cancelled="creating = false"
            />
            <div v-else class="d-flex" style="gap: 10px">
                <button @click="creating = true" class="btn btn-secondary">
                    <i class="fal fa-add"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.active-color {
    color: var(--primary-color);
}
</style>
<script setup lang="ts">
import axios from "axios";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import SbSpinner from "../sb/SbSpinner.vue";
import SbTableLite from "../sb/SbTableLite.vue";

type FieldValue = Record<string, any> & { enabled: boolean; quoteFieldName?: string | null };
type TableRow = { index: number; toggleDetails: () => void };
interface Props {
    formId: number; definitions?: FieldValue[]; inputTypes?: unknown[]; fieldTypes?: unknown[];
    values?: FieldValue[]; quoteFields?: Array<{ name: string }>;
}
const props = withDefaults(defineProps<Props>(), { definitions: () => [], values: () => [], quoteFields: () => [] });
const fields = [
    { key: "links", label: "" }, { key: "label", label: "Fältnamn" },
    { key: "placeholder", label: "Visningstext" }, { key: "settings", label: "Inställningar" },
    { key: "name", label: "Systemnamn" }, { key: "fieldType", label: "Fälttyp" },
    { key: "type", label: "Typ" }, { key: "quoteFieldName", label: "Offert fältnamn" },
];
const creating = ref(false);
const updating = ref(false);
const loadingTables = ref(false);
defineExpose({ loadingTables });
const selectedFieldValues = ref<FieldValue[]>([]);
const availableFieldValues = ref<FieldValue[]>([]);
let submitButton: HTMLButtonElement | null = null;
const allFieldValues = computed(() => [...selectedFieldValues.value, ...availableFieldValues.value]);
const availableQuoteFields = computed(() => props.quoteFields.filter((quoteField) =>
    !allFieldValues.value.some((fieldValue) => fieldValue.quoteFieldName === quoteField.name)));

function removeFromAvailable(index: number): FieldValue { return availableFieldValues.value.splice(index, 1)[0]; }
function moveToSelected(index: number): void { const value = removeFromAvailable(index); value.enabled = true; selectedFieldValues.value.push(value); }
function moveToAvailable(index: number): void { const value = selectedFieldValues.value.splice(index, 1)[0]; value.enabled = false; availableFieldValues.value.push(value); }
function handleFieldEdit(row: TableRow, payload: FieldValue): void { selectedFieldValues.value[row.index] = payload; row.toggleDetails(); if (!payload.enabled) moveToAvailable(row.index); }
function handleFieldCreated(payload: FieldValue): void { availableFieldValues.value.push(payload); creating.value = false; }
function swapValues(i: number, j: number): void { selectedFieldValues.value[j] = selectedFieldValues.value.splice(i, 1, selectedFieldValues.value[j])[0]; }
function handleMoveUp(index: number): void { if (index >= 1) swapValues(index - 1, index); }
function handleMoveDown(index: number): void { if (index < props.values.length) swapValues(index + 1, index); }
function customFieldTemplate(): FieldValue { return { name: "", label: "", type: null, fieldType: "custom", required: false, enabled: false, placeholder: "", quoteFieldName: null }; }
function toggleSubmitButton(): void { if (submitButton) submitButton.disabled = !submitButton.disabled; }
async function submit(): Promise<void> {
    updating.value = true; toggleSubmitButton();
    try {
        await axios.post(route("forms.updateFieldAttributes", props.formId), { fields: allFieldValues.value });
        window.location.reload();
    } catch (error) {
        console.log(error); toggleSubmitButton(); updating.value = false;
    }
}
function onSubmitClick(): void { void submit(); }
onMounted(() => {
    loadingTables.value = true;
    selectedFieldValues.value = props.values.length ? props.values.filter((value) => value.enabled) : [];
    availableFieldValues.value = props.values.length ? props.values.filter((value) => !value.enabled) : [...props.definitions];
    loadingTables.value = false;
    submitButton = document.getElementById("form-fields-submit-btn") as HTMLButtonElement | null;
    if (submitButton) { submitButton.addEventListener("click", onSubmitClick); toggleSubmitButton(); }
});
onBeforeUnmount(() => submitButton?.removeEventListener("click", onSubmitClick));
</script>
